import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@design-system';
import { useSearchParams } from 'react-router-dom';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  FeatureFlagsForm,
  ReportingAttributesForm,
  AccountIdentifiersForm,
  DeleteAccountForm,
  PreRegisteredFeatureFlagsForm,
} from '@/features/wizard-advisor-admin';

const DEFAULT_TAB = 'account-identifiers';

export function WizardAdvisorAdminPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSection = searchParams.get('tab') ?? DEFAULT_TAB;

  const onTabChange = (tab: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Admin Portal"
        description="Manage advisor feature flags and reporting attributes"
      />

      <Tabs
        value={selectedSection}
        onValueChange={onTabChange}
      >
        <TabsList className="mb-6">
          <TabsTrigger value="account-identifiers">Account Identifiers</TabsTrigger>
          <TabsTrigger value="feature-flags">Feature Flags</TabsTrigger>
          <TabsTrigger value="pre-registered-flags">Pre-Registered Flags</TabsTrigger>
          <TabsTrigger value="reporting-attributes">Reporting Attributes</TabsTrigger>
          <TabsTrigger
            value="delete-account"
            className="text-red-600 data-[state=active]:text-red-700"
          >
            Delete Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feature-flags">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Feature Flags</CardTitle>
              <CardDescription className="text-gray-500">
                Search for an advisor by ID or email, then manage their feature flags.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pre-registered-flags">
          <Card className="max-w-2xl border shadow-sm">
            <CardHeader>
              <CardTitle>Pre-Registered Feature Flags</CardTitle>
              <CardDescription className="text-muted-foreground">
                Pre-register emails so feature flags are automatically applied when the advisor
                signs up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PreRegisteredFeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reporting-attributes">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Advisor Attributes</CardTitle>
              <CardDescription className="text-gray-500">
                Add, update, or delete advisor reporting attributes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ReportingAttributesForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account-identifiers">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Update Account Identifiers</CardTitle>
              <CardDescription className="text-gray-500">
                Update an advisor's email address or phone number.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AccountIdentifiersForm />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="delete-account">
          <Card className="max-w-2xl border border-red-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-red-700">Delete Advisor Account</CardTitle>
              <CardDescription className="text-red-500">
                Permanently delete an advisor account and all associated data. This action cannot be
                undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DeleteAccountForm />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
